/**
 * Owner-only billing settings page.
 *
 * Reads the authoritative Firestore subscription mirror (server side has
 * already resolved entitlement — we only DISPLAY it in Sprint B, never
 * gate on it). "Manage subscription" opens a Stripe-hosted Billing Portal
 * session; all lifecycle mutations happen inside Stripe and return via
 * webhook.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CreditCard, ExternalLink, Loader2, ShieldCheck, AlertTriangle,
  CalendarClock, Sparkles,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { useRole } from '../context/RoleContext';
import { PLAN_CATALOG } from '../services/saasBillingService';
import {
  getSubscription,
  getSeatUsage,
  createPortalSession,
  statusLabel,
} from '../services/saasBillingService';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return '—'; }
}

const STATUS_TONE = {
  active:                 { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: ShieldCheck },
  trialing:               { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-800',     icon: Sparkles },
  internal_grandfathered: { bg: 'bg-neutral-50', border: 'border-neutral-200', text: 'text-neutral-800', icon: ShieldCheck },
  past_due:               { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   icon: AlertTriangle },
  canceled:               { bg: 'bg-neutral-50', border: 'border-neutral-200', text: 'text-neutral-700', icon: AlertTriangle },
  unpaid:                 { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800',     icon: AlertTriangle },
  incomplete:             { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   icon: AlertTriangle },
};

export default function BillingSettings() {
  const navigate = useNavigate();
  const { role, loading: roleLoading } = useRole();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [seatUsage, setSeatUsage] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const isOwner = role === 'owner';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getSubscription();
        if (alive) setState({ loading: false, data, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, data: null, error: e.message || 'Failed to load' });
      }
      try {
        const usage = await getSeatUsage();
        if (alive) setSeatUsage(usage);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, []);

  async function openPortal() {
    if (!isOwner) {
      toast.error('Only the account owner can manage billing.');
      return;
    }
    setOpeningPortal(true);
    try {
      const res = await createPortalSession();
      if (res?.url) {
        window.location.assign(res.url);
        return;
      }
      toast.error('Could not open Stripe Billing Portal.');
    } catch (e) {
      if (e.status === 403) {
        toast.error('Only the account owner can manage billing.');
      } else {
        toast.error(e.message || 'Could not open Stripe Billing Portal.');
      }
    } finally {
      setOpeningPortal(false);
    }
  }

  if (state.loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50" data-testid="billing-settings-loading">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-neutral-50 px-6 py-16" data-testid="billing-settings-forbidden">
        <div className="max-w-lg mx-auto rounded-2xl border border-neutral-200 bg-white p-10 text-center">
          <ShieldCheck className="h-8 w-8 text-neutral-400 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-neutral-900">Billing is managed by the owner.</h1>
          <p className="mt-2 text-neutral-600 text-sm">
            Ask the account owner if you need to change plans, update the payment method, or view invoices.
          </p>
          <Button className="mt-6" variant="secondary"
                  onClick={() => navigate('/dashboard')}
                  data-testid="billing-settings-goto-dashboard-btn">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  const sub    = state.data?.subscription || null;
  const status = sub?.status || null;
  const tone   = STATUS_TONE[status] || { bg: 'bg-neutral-50', border: 'border-neutral-200', text: 'text-neutral-700', icon: CreditCard };
  const StatusIcon = tone.icon;
  const noSub = !sub;

  // Derive trial days remaining (whole days, floor).
  const trialDaysRemaining = (() => {
    if (status !== 'trialing' || !sub?.trialEnd) return null;
    const end = new Date(sub.trialEnd);
    if (Number.isNaN(end.getTime())) return null;
    const diffMs = end.getTime() - Date.now();
    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  })();

  // Resolve plan card from canonical catalog (price + display name).
  const planCard = sub?.planKey
    ? PLAN_CATALOG.find((p) => p.planKey === String(sub.planKey).toLowerCase())
    : null;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="billing-settings-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900" data-testid="billing-settings-heading">
            Billing
          </h1>
          <p className="mt-1 text-neutral-500">
            Manage your CleanBid subscription, payment method, and invoices.
          </p>
        </header>

        {noSub ? (
          <div className="rounded-2xl border border-neutral-200 bg-white p-8"
               data-testid="billing-settings-no-subscription">
            <h2 className="text-lg font-semibold text-neutral-900">No active subscription</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Pick a plan to activate CleanBid for your team.
            </p>
            <Button className="mt-6" onClick={() => navigate('/pricing')}
                    data-testid="billing-settings-choose-plan-btn">
              Choose a plan
            </Button>
          </div>
        ) : (
          <>
            <section className={[
              'rounded-2xl border p-6 mb-6',
              tone.bg, tone.border,
            ].join(' ')}
               data-testid="billing-settings-status-card">
              <div className="flex items-start gap-3">
                <StatusIcon className={`h-5 w-5 mt-0.5 ${tone.text}`} />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${tone.text}`}
                     data-testid="billing-settings-status-label">
                    Status: {statusLabel(status)}
                  </p>
                  {status === 'trialing' && sub.trialEnd && (
                    <p className={`text-sm mt-1 ${tone.text}`} data-testid="billing-settings-trial-end">
                      Trial ends {fmtDate(sub.trialEnd)}
                      {trialDaysRemaining != null && (
                        <> · <span data-testid="billing-settings-trial-days-remaining">{trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'} remaining</span></>
                      )}
                      . After that you&apos;ll be charged for {planCard?.name || sub.planKey || 'your plan'}.
                    </p>
                  )}
                  {status === 'past_due' && (
                    <p className={`text-sm mt-1 ${tone.text}`} data-testid="billing-settings-past-due-note">
                      Your last payment failed. Update your payment method to keep access.
                    </p>
                  )}
                  {status === 'canceled' && (
                    <>
                      <p className={`text-sm mt-1 ${tone.text}`} data-testid="billing-settings-canceled-note">
                        Your subscription is cancelled. Pick a new plan to reactivate.
                      </p>
                      <Button
                        className="mt-3"
                        size="sm"
                        onClick={() => navigate('/pricing')}
                        data-testid="billing-settings-reactivate-btn"
                      >
                        Choose a plan
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-neutral-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">Current plan</h2>
                  <div className="mt-2 flex flex-wrap items-baseline gap-3">
                    <span
                      className="inline-flex items-center rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-sm font-semibold text-emerald-900"
                      data-testid="billing-settings-plan-name"
                    >
                      {planCard?.name || (sub.planKey ? sub.planKey.charAt(0).toUpperCase() + sub.planKey.slice(1) : 'Plan')}
                      {status === 'trialing' && (
                        <span className="ml-1.5 text-xs font-normal text-emerald-700">— 14-day trial</span>
                      )}
                    </span>
                    {planCard && (
                      <span className="text-sm text-neutral-600" data-testid="billing-settings-plan-price">
                        <span className="font-semibold text-neutral-900">{planCard.priceLabel}</span>{planCard.cadence}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-neutral-500">Plan</dt>
                  <dd className="mt-0.5 font-medium text-neutral-900 capitalize"
                      data-testid="billing-settings-plan-key">
                    {sub.planKey || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Cancel at period end</dt>
                  <dd className="mt-0.5 font-medium text-neutral-900"
                      data-testid="billing-settings-cancel-at-period-end">
                    {sub.cancelAtPeriodEnd ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Current period ends</dt>
                  <dd className="mt-0.5 font-medium text-neutral-900"
                      data-testid="billing-settings-period-end">
                    {fmtDate(sub.currentPeriodEnd)}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-500">Trial ends</dt>
                  <dd className="mt-0.5 font-medium text-neutral-900"
                      data-testid="billing-settings-trial-end-display">
                    {fmtDate(sub.trialEnd)}
                  </dd>
                </div>
              </dl>

              {/* Sprint 306 — Seat usage panel */}
              {seatUsage && (
                <div
                  className={[
                    'mt-6 rounded-lg border p-4',
                    seatUsage.overLimit
                      ? 'border-rose-200 bg-rose-50'
                      : seatUsage.atLimit
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-neutral-200 bg-neutral-50',
                  ].join(' ')}
                  data-testid="billing-seat-usage"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Team seats</p>
                      <p className="mt-1 text-base font-semibold text-neutral-900" data-testid="billing-seat-usage-line">
                        {seatUsage.unlimited
                          ? `${seatUsage.activeMembers} user${seatUsage.activeMembers === 1 ? '' : 's'} · Unlimited`
                          : (seatUsage.pendingInvites > 0
                              ? `${seatUsage.activeMembers} active + ${seatUsage.pendingInvites} pending of ${seatUsage.maxUsers} seats`
                              : `${seatUsage.activeMembers} of ${seatUsage.maxUsers} users`)}
                      </p>
                      {seatUsage.overLimit && (
                        <p className="mt-2 text-xs text-rose-900" data-testid="billing-seat-usage-overlimit-msg">
                          You are above the seat limit for your current plan. Existing users still have access — remove users or upgrade to add new ones.
                        </p>
                      )}
                      {!seatUsage.overLimit && seatUsage.atLimit && seatUsage.upgradeCopy && (
                        <p className="mt-2 text-xs text-amber-900" data-testid="billing-seat-usage-atlimit-msg">
                          {seatUsage.upgradeCopy}
                        </p>
                      )}
                    </div>
                    {isOwner && !seatUsage.unlimited && (seatUsage.atLimit || seatUsage.overLimit) && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate('/pricing')}
                        data-testid="billing-seat-usage-upgrade-btn"
                      >
                        Upgrade plan
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  onClick={openPortal}
                  disabled={openingPortal}
                  data-testid="billing-settings-manage-subscription-btn"
                >
                  {openingPortal ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                  Manage subscription
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate('/pricing')}
                  data-testid="billing-settings-change-plan-btn"
                >
                  Change plan
                </Button>
              </div>
              <p className="mt-4 text-xs text-neutral-400">
                &quot;Manage subscription&quot; opens the Stripe Billing Portal in a new context — updates take a
                moment to reflect here after the webhook delivers.
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
